
import { Component, Vue } from "vue-property-decorator";
import EmployeeSummaryDetails from "./components/EmployeeSummaryDetails.vue";
import EmployeeSummaryStatus from "./components/EmployeeSummaryStatus.vue";
import EmployeeSummaryInfo from "./components/EmployeeSummaryInfo.vue";
import EmployeeSummaryCompliance from "./components/EmployeeSummaryCompliance.vue";
import { API_TTask, convertTaskForTable, TTask_Table } from "@/models/tasks";
import { TEmployeeSummary, TEmployeePayload } from "@/models/employees";
import { API_ROUTES } from "@/utils/API";
import { SMApp, SMUser } from "@/store";
import {
  EMPLOYEE_EMPLOYMENT_TYPE_SPONSORED,
  EMPLOYEE_EMPLOYMENT_STATUS_EMPLOYED,
  EMPLOYEE_EMPLOYMENT_STATUS_LEAVING,
} from "@/constants/compliance";

Component.registerHooks(["beforeRouteEnter"]);

@Component({
  components: {
    EmployeeSummaryDetails,
    EmployeeSummaryInfo,
    EmployeeSummaryStatus,
    EmployeeSummaryCompliance,
    EmployeeSummaryTasksTable: () => import("./components/EmployeeSummaryTasksTable.vue"),
    EmployeeSummaryMessage: () => import("./components/EmployeeSummaryMessage.vue"),
    TerminationPopup: () => import("@/components/Popups/TerminationPopup.vue"),
  },
})
export default class EmployerEmployeesTemplateSummary extends Vue {
  employeeSummary: TEmployeeSummary = ({} as unknown) as TEmployeeSummary;

  tasksEmployee: TTask_Table[] = [] as TTask_Table[];

  tasksEmployer: TTask_Table[] = [] as TTask_Table[];

  init = false;

  mounted() {
    this.$root.$on("update-employee-level-data", this.fetchEmployeeSummary);
  }

  async beforeRouteEnter(to: any, from: any, next: any) {
    next(async (vm: any) => {
      await vm.fetchEmployeeSummary();
    });
  }

  get employeeId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  get companyId(): number {
    return Number(this.$route.params.company_id ?? 0);
  }

  get endpoint() {
    const { companyId, employeeId: id } = this;

    if (!id) return "";

    if (companyId) return API_ROUTES.EXPERT_COMPANIES_EMPLOYEES_SUMMARY(companyId, id);

    return API_ROUTES.EMPLOYEE_SUMMARY(id);
  }

  loading = false;

  async fetchEmployeeSummary() {
    if (!this.endpoint) return;

    try {
      this.loading = true;

      const { data } = await this.axios.get(this.endpoint);

      if (!data) return;

      this.employeeSummary = data;

      const { tasks, employer_tasks, working_status } = this.employeeSummary;

      this.setTasksEmployee(tasks);
      this.setTasksEmployer(employer_tasks);

      SMApp.setEmployeeWorkingStatus(working_status);
    } finally {
      this.init = true;

      this.componentKey += 1;

      this.loading = false;
    }
  }

  setTasksEmployee(tasks: API_TTask[]) {
    if (!tasks?.length) {
      return (this.tasksEmployee = []);
    }

    this.tasksEmployee = tasks.map((task) => convertTaskForTable(task));
  }

  setTasksEmployer(tasks: API_TTask[]) {
    if (!tasks?.length) {
      return (this.tasksEmployer = []);
    }

    this.tasksEmployer = tasks.map((task) => convertTaskForTable(task));
  }

  isExpert() {
    return SMUser.isExpert;
  }

  isMessageHidden = false;

  get showMessage() {
    if (this.isMessageHidden) return false;

    if (SMUser.isExpert) return false;

    if (!this.employeeSummary?.reason_of_termination) return false;

    if (this.employeeSummary?.employment_type !== EMPLOYEE_EMPLOYMENT_TYPE_SPONSORED) return false;

    if (localStorage.getItem("show_employer_employee_summary_msg") === "false") return false;

    return true;
  }

  hideMessage() {
    this.isMessageHidden = true;

    localStorage.setItem("show_employer_employee_summary_msg", "false");
  }

  isPopupOpen = false;

  openPopup() {
    this.isPopupOpen = true;
  }

  closePopup() {
    this.isPopupOpen = false;
  }

  componentKey = 0;

  cancel() {
    this.componentKey += 1;
    this.closePopup();
  }

  get endpointEmployeeLeave() {
    const { companyId, employeeId: id } = this;

    if (!id) return "";

    if (companyId) return API_ROUTES.EXPERT_COMPANY_EMPLOYEE_LEAVE(companyId, id);

    return API_ROUTES.EMPLOYEE_LEAVE(id);
  }

  async save(data: TEmployeePayload) {
    try {
      const leaving_date = data?.leaving_date || "";

      let status = EMPLOYEE_EMPLOYMENT_STATUS_LEAVING;

      if (!leaving_date) status = EMPLOYEE_EMPLOYMENT_STATUS_EMPLOYED;

      const payload = {
        status,
        leaving_date,
      };

      await this.axios.post(this.endpointEmployeeLeave, payload);

      this.employeeSummary.work_end_date = leaving_date;
      this.employeeSummary.working_status = status;

      this.componentKey += 1;
    } finally {
      await this.fetchEmployeeSummary();

      this.closePopup();
    }
  }
}
